// Raise a work order by hand, or change one that has not been released yet.
// One form for both: the product is fixed once the order exists, everything else
// stays editable until release.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { modalLabel } from '../components/review/fieldStyles';
import { buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import Dialog from '../components/ui/Dialog';
import { Field, TextArea } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { useToast } from '../components/Toast/ToastContext';
import { useBomsApi, bomErrorMessage, variantLabel, productName, productCode, type ProductSummary } from '../Parts/bomsApi';
import {
  useWorkOrdersApi,
  workOrderErrorMessage,
  type WorkOrder,
  type WorkOrderDetail,
} from './workOrdersApi';

interface Props {
  onClose: () => void;
  /** Present when editing: the order whose quantity, build, date and notes change. */
  editing?: WorkOrder;
  /** A sales order to raise this against; the field shows only when given. */
  salesOrderId?: string | number;
  onSaved?: (detail: WorkOrderDetail) => void;
}

/** The builds a product has; absent on an older API row, which shows no Build field. */
const variantsOf = (m: ProductSummary | undefined): string[] =>
  m && Array.isArray(m.variants) ? m.variants : [];

const NewWorkOrderDrawer: React.FC<Props> = ({ onClose, editing, salesOrderId, onSaved }) => {
  const woApi = useWorkOrdersApi();
  const boms = useBomsApi();
  const toast = useToast();
  const history = useHistory();

  const [models, setModels] = useState<ProductSummary[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [productId, setProductId] = useState(editing ? String(editing.output_part_id) : '');
  const [variant, setVariant] = useState<string>(editing?.variant ?? '');
  const [quantity, setQuantity] = useState(editing ? String(editing.quantity) : '');
  const [plannedDate, setPlannedDate] = useState(editing?.planned_date ?? '');
  const [soId, setSoId] = useState(salesOrderId != null ? String(salesOrderId) : '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // What the dialog opened with. The product and build the form picks for itself once the
  // list arrives count as opened-with; only a pick by the person is typed work.
  const openedProduct = useRef(productId);
  const openedVariant = useRef(variant);

  useEffect(() => {
    boms
      .listProducts()
      .then((rows) => {
        const list = (rows || []).filter((m) => m.is_active || m.id === editing?.output_part_id);
        setModels(list);
        const firstBuildable = list.find((m) => m.line_count > 0);
        if (firstBuildable && !productId) {
          openedProduct.current = String(firstBuildable.id);
          setProductId(String(firstBuildable.id));
        }
      })
      .catch((err) => setModelsError(bomErrorMessage(err, 'The product list did not load. Close this and try again.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const model = models?.find((m) => String(m.id) === productId);
  const variants = useMemo(() => variantsOf(model), [model]);

  // A model with builds needs one picked: an order with no build only draws the
  // untagged lines and would leave every tagged part off the drone.
  useEffect(() => {
    if (variants.length === 0) return;
    if (!variants.includes(variant)) {
      const chosen = variants.includes('standard') ? 'standard' : variants[0];
      if (productId === openedProduct.current) openedVariant.current = chosen;
      setVariant(chosen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants]);

  const dirty =
    productId !== openedProduct.current ||
    variant !== openedVariant.current ||
    quantity !== (editing ? String(editing.quantity) : '') ||
    plannedDate !== (editing?.planned_date ?? '') ||
    soId !== (salesOrderId != null ? String(salesOrderId) : '') ||
    notes.trim() !== (editing?.notes ?? '').trim();

  const submit = async () => {
    const qty = Number(quantity);
    if (!editing && !productId) {
      setError('Pick the product to build.');
      return;
    }
    if (!Number.isInteger(qty) || qty <= 0) {
      setError('Quantity is how many to build, a whole number above zero.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const shared = {
        quantity: qty,
        variant: variants.length ? variant : null,
        planned_date: plannedDate || null,
        notes: notes.trim() || null,
      };
      if (editing) {
        // Until the models load the builds are unknown; leave the build alone
        // rather than clearing it.
        const patch = models ? shared : { ...shared, variant: undefined };
        const detail = await woApi.update(editing.id, patch);
        toast.success(`Saved ${editing.number}.`);
        onSaved?.(detail);
        onClose();
      } else {
        const detail = await woApi.create({
          output_part_id: Number(productId),
          ...shared,
          sales_order_id: soId.trim() || null,
        });
        toast.success(`Started ${detail.order.number}.`);
        onSaved?.(detail);
        history.push(`/work-orders/${detail.order.id}`);
      }
    } catch (err) {
      setError(workOrderErrorMessage(err, editing ? 'The changes did not save. Try again.' : 'The work order was not created. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Dialog
      open
      title={editing ? `Edit ${editing.number}` : 'New work order'}
      onClose={onClose}
      size="lg"
      busy={busy}
      dirty={dirty}
      onSubmit={submit}
      problem={error}
      footer={
        <>
          <button type="button" onClick={onClose} className={buttonClass('quiet', 'md')}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || (!editing && !models)}
            className={buttonClass('filled', 'md')}
          >
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create work order'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {editing ? (
          <div>
            <div className={modalLabel}>Product</div>
            <div className="text-sm text-on-surface">
              {editing.model_code ?? editing.model_name}
              {editing.model_code && editing.model_name && <span className="text-on-surface-variant">, {editing.model_name}</span>}
            </div>
          </div>
        ) : (
          <div className="block">
            <span className={modalLabel}>Product</span>
            {modelsError ? (
              <Alert tone="error">{modelsError}</Alert>
            ) : (
              <Select
                label="Product"
                // Dialog looks for this marker; the Select forwards its ref to the trigger button.
                ref={(el) => el?.setAttribute('data-drawer-autofocus', '')}
                value={productId || null}
                disabled={!models}
                placeholder={!models ? 'Loading products…' : models.length === 0 ? 'No products yet' : 'Choose a product'}
                options={(models ?? []).map((m) => ({
                  key: String(m.id),
                  label:
                    m.line_count > 0
                      ? `${productName(m)} (${productCode(m)})`
                      : `${productName(m)} (${productCode(m)}), no parts list`,
                  disabled: m.line_count === 0,
                }))}
                onChange={(key) => {
                  setProductId(key);
                  setError(null);
                }}
              />
            )}
            <Reveal show={!!models && models.some((m) => m.line_count === 0)}>
              <span className="mt-1 block text-xs text-on-surface-variant">
                A product needs a parts list before it can be built. Start one on its Product BOMs page.
              </span>
            </Reveal>
          </div>
        )}

        <Reveal show={variants.length > 0}>
          <div className="block p-1">
            <span className={modalLabel}>Build</span>
            <Select
              label="Build"
              value={variant || null}
              options={variants.map((v) => ({ key: v, label: variantLabel(v) }))}
              onChange={setVariant}
            />
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className={modalLabel}>Quantity</span>
            <Field
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={quantity}
              data-drawer-autofocus={editing ? true : undefined}
              placeholder="How many"
              onChange={(e) => {
                setQuantity(e.target.value.replace(/[^0-9]/g, ''));
                setError(null);
              }}
              onKeyDown={onEnter}
              className="tabular-nums"
            />
          </label>
          <label className="block">
            <span className={modalLabel}>Planned date</span>
            <Field
              type="date"
              value={plannedDate}
              onChange={(e) => setPlannedDate(e.target.value)}
              onKeyDown={onEnter}
            />
          </label>
        </div>

        {salesOrderId != null && !editing && (
          <label className="block">
            <span className={modalLabel}>Sales order</span>
            <Field
              type="text"
              autoComplete="off"
              value={soId}
              onChange={(e) => setSoId(e.target.value)}
              onKeyDown={onEnter}
            />
          </label>
        )}

        <label className="block">
          <span className={modalLabel}>Notes</span>
          <TextArea
            rows={2}
            value={notes}
            maxLength={2000}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
      </div>
    </Dialog>
  );
};

export default NewWorkOrderDrawer;
